/**
 * Is this point at sea? — the land/water predicate the placement chain never
 * had.
 *
 * `docs/engineering/london-sea-placements.md` §3.3 found that **nothing**
 * anywhere in the port's placement chain consulted terrain: not the spawn, not
 * the flak belt, not the aerodrome aprons, not the ground targets. The one
 * `SurfaceKind` that would have caught it — `flight/ground.ts`'s `"water"`,
 * gear limit 0 — was unreachable, because the only `surfaceAt` provider ever
 * constructed returns `"aerodrome"` or `"field"`. This module is the missing
 * half. `flight/fly-region.ts` builds one mask per region and three consumers
 * read it: the region origin (so a flight never starts wet), the aerodrome
 * aprons (so `withLandingFields` cannot mark the North Sea as prepared ground)
 * and the sim's own `GroundQuery` (so `"water"` finally means something).
 *
 * ## The rule is a topmost-surface test, not a coverage test
 *
 * This is `london-sea-placements.md` §8.6's second residual, and it is the
 * reason the first version of this module reported a hundred London buildings
 * standing in the sea. `TERR1-00` **is** the sea — verified under DOSBox-X in
 * §8.2 — and it runs a long way *under* the coast, so "is any water triangle
 * here" says water on dry land. It said so at the old bake scale and it still
 * says so at the corrected one.
 *
 * The original has no depth buffer. Everything is coplanar at `z = 0` and the
 * visible surface at a point is simply the **last triangle covering it in
 * `.WLD` placement order**, over the flat default ground where nothing covers.
 * `TERR1-00` is placement **#0** — the first thing drawn — and every landcover
 * plate, town pad and field paints over it. So the predicate is *"the last
 * triangle in `.WLD` order covering this point is `anchorIndex === 4`"*, which
 * is what this module now implements, and what reproduces the original's exact
 * palette entry at 349 of 361 sampled London points and 355 of 361 Verdun ones
 * (§8.4, §9).
 *
 * ## Why not nonzero winding
 *
 * The previous version filled by winding number, on the reasoning that the
 * plates are fan-triangulated and a concave shore fanned from one vertex emits
 * triangles that spill outside their own polygon. That is true, but it is true
 * of the **original's own renderer** too — it draws the same fan the same way
 * — so reproducing the spill is more faithful than cancelling it, and the
 * raster scores above are measured against what the original actually paints.
 * Plain containment is also what makes a topmost test meaningful: a winding
 * number has no "which triangle won".
 *
 * ## Which triangles are water
 *
 * The same rule the in-flight map uses and no list of object names: **a
 * triangle whose `anchorIndex` is 4 is water** (`map/map-features.ts`). That
 * takes in the two `s = 10` sea plates, London's Thames estuary, Verdun's
 * ponds and every river alike. The map and the 3D world already share this
 * predicate vertex for vertex (`map/map-water.ts`); this makes it a third
 * consumer of the same bytes rather than a second opinion.
 *
 * ## Why it is bucketed
 *
 * A region carries several thousand ground triangles and the mask is asked
 * thousands of questions while a region loads (the origin search alone probes a
 * lattice per candidate). A uniform grid over the small triangles plus a flat
 * list for the handful of region-scale ones — a sea plate's own triangles are
 * hundreds of kilometres across and would otherwise paint millions of cells —
 * keeps a query at a few dozen tests.
 */

import { bestDrawableLod, type BakedModelBody } from "../three/baked-model.ts";
import { WATER_PALETTE_INDEX } from "../map/map-features.ts";
import { modelFrameToWorld, placementWorldTransform, ORIGIN_ZERO, type PlacementLike, type PlacementOrigin } from "./placement-transform.ts";

/**
 * The scale exponent the two sea plates carry and nothing else does — `s = 10`.
 * Kept as a `>=` threshold at 9 so a third plate at some intermediate exponent
 * would still read as a plate rather than as a river.
 */
export const SEA_SCALE_EXPONENT = 9;

/**
 * How far below the datum an `s = 10` sea plate is drawn.
 *
 * `london-sea-placements.md` §1.4: the plate is a flat quad at `y = 0`,
 * exactly coplanar with every town's ground pad, whose own `y` is also 0 (the
 * flat-plane verdict, world-terrain.md §5). `three/decal-layers.ts`'s 4 mm
 * nudge is computed *per model*, so it separates faces inside one object and
 * does nothing across objects — a town and the sea z-fight.
 *
 * §8.6 makes this **load-bearing** rather than cosmetic. At the corrected scale
 * the plate is 936 x 607 km of one flat colour coplanar with everything else,
 * so the coplanar area that can z-fight grows by 6x; and the original's own
 * answer is now known and unambiguous — the sea is **under** the land, and the
 * land wins wherever it covers (§8.2, §8.4). A metre of draught encodes exactly
 * that, mirrors what the map already does by filling water first, and is
 * invisible from an aeroplane because the land is opaque and hides the step at
 * the shore.
 */
export const SEA_PLATE_DEPTH_M = 1;

/**
 * One ground triangle on the plane, in recentred world metres: three `(x, z)`
 * corners then `1` for water and `0` for land. **Array order is draw order**,
 * which is `.WLD` placement order and, within a placement, the model's own
 * triangle order.
 */
export type SurfaceTriangle = readonly [number, number, number, number, number, number, number];

/** Cell side of the uniform index. Comfortably finer than a river's meanders and coarser than its quads. */
const BUCKET_M = 2_000;

/** A triangle spanning more cells than this in either axis skips the grid and is always tested. */
const MAX_BUCKET_SPAN = 8;

export interface WaterMask {
  /** True when the topmost surface at `(x, z)` — recentred world metres — is water. */
  isWater(x: number, z: number): boolean;
  /** How many water triangles the region contributed. Diagnostics, and what the tests pin. */
  readonly triangleCount: number;
  /** How many ground triangles of any kind the topmost test ranges over. */
  readonly surfaceCount: number;
}

/** A region with no ground at all, and the honest answer for a caller that has no mask. */
export const DRY_EVERYWHERE: WaterMask = { isWater: () => false, triangleCount: 0, surfaceCount: 0 };

/** Sign of the side of `b -> a` a point falls on; all three alike means inside, whichever way the triangle winds. */
function edgeSign(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  return (px - bx) * (az - bz) - (ax - bx) * (pz - bz);
}

function contains(t: SurfaceTriangle, px: number, pz: number): boolean {
  const d1 = edgeSign(px, pz, t[0], t[1], t[2], t[3]);
  const d2 = edgeSign(px, pz, t[2], t[3], t[4], t[5]);
  const d3 = edgeSign(px, pz, t[4], t[5], t[0], t[1]);
  return !((d1 < 0 || d2 < 0 || d3 < 0) && (d1 > 0 || d2 > 0 || d3 > 0));
}

/** A zero-area triangle covers nothing — it is a fan artefact, not a surface. */
function degenerate(t: SurfaceTriangle): boolean {
  return (t[2] - t[0]) * (t[5] - t[1]) - (t[3] - t[1]) * (t[4] - t[0]) === 0;
}

function cellKey(column: number, row: number): number {
  // Two 16-bit halves of one integer: a region is far inside ±65 000 buckets.
  return ((column + 32_768) << 16) | (row + 32_768);
}

/**
 * Every ground triangle of every placed object, stamped into the recentred
 * world frame, **in `.WLD` draw order**.
 */
export function surfaceTrianglesOf(
  placements: readonly (PlacementLike & { readonly objectRef: string | null })[],
  models: ReadonlyMap<string, BakedModelBody>,
  origin: PlacementOrigin = ORIGIN_ZERO,
): SurfaceTriangle[] {
  const out: SurfaceTriangle[] = [];
  for (const placement of placements) {
    if (placement.objectRef === null) continue;
    const body = models.get(placement.objectRef);
    if (!body) continue;
    const lod = bestDrawableLod(body.lods);
    if (!lod || lod.triangleCount === 0) continue;

    const { position, yawRadians } = placementWorldTransform(placement, origin);
    const cos = Math.cos(yawRadians);
    const sin = Math.sin(yawRadians);
    const corner = (vertex: number): readonly [number, number] => {
      const base = vertex * 3;
      const local = modelFrameToWorld(lod.positions[base] ?? 0, lod.positions[base + 1] ?? 0, lod.positions[base + 2] ?? 0);
      return [cos * local.x + sin * local.z + position.x, -sin * local.x + cos * local.z + position.z];
    };
    for (let triangle = 0; triangle < lod.triangleCount; triangle += 1) {
      const a = corner(lod.indices[triangle * 3] ?? 0);
      const b = corner(lod.indices[triangle * 3 + 1] ?? 0);
      const c = corner(lod.indices[triangle * 3 + 2] ?? 0);
      const water = lod.faceColors[triangle]?.anchorIndex === WATER_PALETTE_INDEX ? 1 : 0;
      out.push([a[0], a[1], b[0], b[1], c[0], c[1], water]);
    }
  }
  return out;
}

/**
 * Indexes a surface set into the predicate. Exported so a test can build one
 * from synthetic geometry.
 */
export function surfaceMaskOf(triangles: readonly SurfaceTriangle[]): WaterMask {
  const waterCount = triangles.reduce((n, t) => n + (t[6] === 1 ? 1 : 0), 0);
  if (waterCount === 0) return DRY_EVERYWHERE;

  // Indices into `triangles`, so a bucket hit still knows its draw order.
  const buckets = new Map<number, number[]>();
  const oversized: number[] = [];
  for (const [index, t] of triangles.entries()) {
    if (degenerate(t)) continue;
    const minColumn = Math.floor(Math.min(t[0], t[2], t[4]) / BUCKET_M);
    const maxColumn = Math.floor(Math.max(t[0], t[2], t[4]) / BUCKET_M);
    const minRow = Math.floor(Math.min(t[1], t[3], t[5]) / BUCKET_M);
    const maxRow = Math.floor(Math.max(t[1], t[3], t[5]) / BUCKET_M);
    if (maxColumn - minColumn > MAX_BUCKET_SPAN || maxRow - minRow > MAX_BUCKET_SPAN) {
      oversized.push(index);
      continue;
    }
    for (let column = minColumn; column <= maxColumn; column += 1) {
      for (let row = minRow; row <= maxRow; row += 1) {
        const key = cellKey(column, row);
        const cell = buckets.get(key);
        if (cell) cell.push(index);
        else buckets.set(key, [index]);
      }
    }
  }

  return {
    triangleCount: waterCount,
    surfaceCount: triangles.length,
    isWater(x, z) {
      // The last triangle in draw order that covers the point is the visible
      // surface; the grid and the oversized list each hold a subset of draw
      // order, so take the maximum index over both.
      let top = -1;
      for (const index of oversized) if (index > top && contains(triangles[index]!, x, z)) top = index;
      const cell = buckets.get(cellKey(Math.floor(x / BUCKET_M), Math.floor(z / BUCKET_M)));
      if (cell) for (const index of cell) if (index > top && contains(triangles[index]!, x, z)) top = index;
      return top >= 0 && triangles[top]![6] === 1;
    },
  };
}

/** The region's surface, in the frame `origin` recentres it to. */
export function buildWaterMask(
  placements: readonly (PlacementLike & { readonly objectRef: string | null })[],
  models: ReadonlyMap<string, BakedModelBody>,
  origin: PlacementOrigin = ORIGIN_ZERO,
): WaterMask {
  return surfaceMaskOf(surfaceTrianglesOf(placements, models, origin));
}

/** Shifts a mask built in one frame into a frame recentred by `origin` — cheaper than re-stamping every triangle. */
export function recentreWaterMask(mask: WaterMask, origin: PlacementOrigin): WaterMask {
  if (origin.x === 0 && origin.z === 0) return mask;
  return {
    triangleCount: mask.triangleCount,
    surfaceCount: mask.surfaceCount,
    // `placementWorldTransform`: world `x` is `.WLD x - origin.x` and world `z`
    // is `-(.WLD y - origin.z)`, so undoing the recentring is `+origin.x` on x
    // and `-origin.z` on z.
    isWater: (x, z) => mask.isWater(x + origin.x, z - origin.z),
  };
}
